
import { useState, useEffect } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      // Check if we have a value in localStorage
      if (item) {
        // Parse stored json or return as is if it's not JSON
        try {
          return JSON.parse(item);
        } catch (error) {
          // If the stored value is not valid JSON, return it as-is
          return item as unknown as T;
        }
      }
      return initialValue;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return initialValue;
    }
  });

  // Update localStorage whenever storedValue changes
  useEffect(() => {
    try {
      // Save to local storage
      if (storedValue !== undefined) {
        const valueToStore = typeof storedValue === 'object' 
          ? JSON.stringify(storedValue) 
          : String(storedValue);
        window.localStorage.setItem(key, valueToStore);
      }
    } catch (error) {
      console.error("Error writing to localStorage:", error);
    }
  }, [key, storedValue]);

  // Return a wrapped version of useState's setter function
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      if (valueToStore !== undefined) {
        const storageValue = typeof valueToStore === 'object' 
          ? JSON.stringify(valueToStore) 
          : String(valueToStore);
        window.localStorage.setItem(key, storageValue);
        console.log(`Saved to localStorage: ${key}`, valueToStore);
      }
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  return [storedValue, setValue] as const;
}
